import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ResponseType } from '@vi-iot/client/models';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export const CLIENT_CONFIG = new InjectionToken('oClientConfig');

export interface ClientConfiguration {
  withCredentials: boolean;
  baseUrl: string;
  diagStackPoolManager: string;
  version: string;
}

export const httpHeaders = new HttpHeaders({
  'Content-Type': 'application/vnd.vito+json',
  Accept: 'application/vnd.vito+json',
});

@Injectable()
export class ClientService {
  readonly version: string;
  private readonly withCredentials: boolean;
  private readonly baseUrl: string;

  constructor(
    @Inject(CLIENT_CONFIG) private clientConfiguration: ClientConfiguration,
    private http: HttpClient
  ) {
    this.withCredentials = this.clientConfiguration.withCredentials;
    this.baseUrl = this.clientConfiguration.baseUrl;
    this.version = this.clientConfiguration.version;
  }

  getVersion(version?: string): string {
    return version || this.version;
  }

  get<T>(endpoint: string, params?: any, headers?: HttpHeaders): Observable<T> {
    const url = this.baseUrl + endpoint;

    return this.http
      .get<T>(url, { params, headers, withCredentials: this.withCredentials })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  getDynamicData<T>(
    endpoint: string,
    responseType: ResponseType = ResponseType.json,
    params?: Record<string, string | number>,
    headers?: HttpHeaders
  ): Observable<T> {
    const url = this.baseUrl + endpoint;

    return this.http
      .get<T>(url, {
        params,
        headers,
        responseType: responseType as 'json',
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  post<T>(endpoint: string, body: any, params?: any): Observable<T> {
    const url = this.baseUrl + endpoint;

    return this.http
      .post<T>(url, body, {
        params,
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  put<T>(endpoint: string, body: any, params?: any): Observable<T> {
    const url = this.baseUrl + endpoint;

    return this.http
      .put<T>(url, body, {
        params,
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  patch<T>(endpoint: string, body: any, params?: any): Observable<T> {
    const url = this.baseUrl + endpoint;

    return this.http
      .patch<T>(url, body, {
        params,
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  delete<T>(endpoint: string, params?: any, body?: any): Observable<T> {
    const url = this.baseUrl + endpoint;

    return this.http
      .delete<T>(url, {
        params,
        withCredentials: this.withCredentials,
        body,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawGet<T>(
    baseUrl: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .get<T>(baseUrl, {
        params,
        headers,
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawGetHttp<T>(
    baseUrl: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .get<T>(baseUrl, {
        params,
        headers,
        observe: 'response',
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawGetText<T>(
    baseUrl: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .get<T>(baseUrl, {
        params,
        headers,
        responseType: 'text' as 'json',
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawPost<T>(
    baseUrl: string,
    body: any,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .post<T>(baseUrl, body, {
        params,
        headers,
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawPostText<T>(
    baseUrl: string,
    body: any,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .post<T>(baseUrl, body, {
        params,
        headers,
        responseType: 'text' as 'json',
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawPut<T>(
    baseUrl: string,
    body: any,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .put<T>(baseUrl, body, {
        params,
        headers,
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawPutText<T>(
    baseUrl: string,
    body: any,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .put<T>(baseUrl, body, {
        params,
        headers,
        responseType: 'text' as 'json',
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawPatch<T>(
    baseUrl: string,
    body: any,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .patch<T>(baseUrl, body, {
        params,
        headers,
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawPatchText<T>(
    baseUrl: string,
    body: any,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .patch<T>(baseUrl, body, {
        params,
        headers,
        responseType: 'text' as 'json',
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawDelete<T>(
    baseUrl: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .delete<T>(baseUrl, {
        params,
        headers,
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  rawDeleteText<T>(
    baseUrl: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: HttpHeaders
  ): Observable<T> {
    return this.http
      .delete<T>(baseUrl, {
        params,
        headers,
        responseType: 'text' as 'json',
        withCredentials: this.withCredentials,
      })
      .pipe(catchError(this.handleError)) as Observable<T>;
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      // console.error('An error occurred:', error.error.message);
    } else {
      if (error.status === 0) {
        //   console.warn('There was no response, or the response was blocked by the browser.');
        //   console.error(error);
      }
    }

    // return an observable with a user-facing error message
    return throwError(error.error);
  }
}
